import React, { FC, useEffect, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  CountryBanner as UIFoundationCountryBanner,
  CountryBannerProps as UIFoundationCountryBannerProps
} from '@dcxwam/dcx-wam-ui-foundations'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { useGeolocation } from '../../../../utils/geo-location'
import CountryBannerEditConfig from './CountryBannerEditConfig'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import { CountryBannerProps, CountryBannerResponse } from './types'
import { fetchCountryInfo } from './utils'
import { PageConfig, GeneralConfigType } from '../../../../utils/page-config'

const CountryBanner: FC<EditableComponentPropsType<CountryBannerProps>> =
  withComponentRefresh((props) => {
    const { model, ...rest } = props
    const isAuthor = AuthoringUtils.isInEditor()
    const { market: pagePath } = PageConfig.getConfig() as GeneralConfigType
    const { countryCode: geoCountryCode, isLoading: geoLoading } =
      useGeolocation()
    const [countryData, setCountryData] =
      useState<CountryBannerResponse | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      const loadCountryData = async () => {
        if (geoLoading) {
          return
        }

        if (!geoCountryCode) {
          setIsLoading(false)
          return
        }

        setIsLoading(true)
        const data = await fetchCountryInfo(geoCountryCode, pagePath)
        setCountryData(data)
        setIsLoading(false)
      }

      loadCountryData()
    }, [geoCountryCode, geoLoading])

    const countryBannerProps: UIFoundationCountryBannerProps = {
      notPresentMessage: model.websiteNotAvailableMessage ?? '',
      imageProps: {
        width: '30px'
      },
      countryInfo: countryData ?? undefined
    }

    const editableProps = { model, ...rest }

    const renderContent = () => {
      if (isAuthor) {
        return CountryBannerEditConfig?.configLabel
      }
      if (isLoading || geoLoading) {
        return null
      }
      return <UIFoundationCountryBanner {...countryBannerProps} />
    }

    return (
      <EditableComponent {...editableProps}>
        {renderContent()}
      </EditableComponent>
    )
  })

const CountryBannerWithErrorBoundary = withErrorBoundary(CountryBanner, {
  componentName: 'CountryBanner',
  fallback: <ErrorFallback componentName="CountryBanner" />
})

export default CountryBannerWithErrorBoundary
